import React, { useState } from 'react'
import { UserCheck, Brain, Sparkles, Target, Layers, Zap, Shield, CheckCircle, MapPin, Code, Menu, X } from 'lucide-react'

const ExpertInTheLoop = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const navColors = {
    services: 'from-blue-400 to-blue-600',
    products: 'from-purple-400 to-purple-600',
    research: 'from-pink-400 to-red-500',
    news: 'from-orange-400 to-red-500',
    about: 'from-yellow-400 to-yellow-600'
  }

  const Header = () => (
    <header className="fixed top-0 w-full z-50 bg-black/10 backdrop-blur-2xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center space-x-2">
            <div className="relative">
              <div className="absolute inset-0 rounded-full blur-md opacity-40 bg-red-500 animate-pulse" />
              <img
                src="/icon_forgen_512.png"
                alt="ForGen AI Logo"
                className="relative w-10 h-10 object-contain z-10"
              />
            </div>
            <a href="/" className="text-2xl font-bold text-white hover:text-red-400 transition-colors">
              ForGen AI
            </a>
          </div>

          <nav className="hidden md:flex space-x-8">
          {['Services', 'Products', 'Research', 'News', 'About']
            .filter(item => item !== 'Transportation')
            .map((item) => (
              <a
                key={item}
                href={`/#${item.toLowerCase()}`}
                className={`relative text-white hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r ${
                  navColors[item.toLowerCase()]
                } transition-all duration-300 group`}
              >
                {item}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r ${
                  navColors[item.toLowerCase()]
                } transition-all duration-300 group-hover:w-full`}></span>
              </a>
          ))}
          <a
            href="/expert-in-the-loop"
            className="relative text-white hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-300 group"
          >
            Expert-in-the-Loop™
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-300 group-hover:w-full"></span>
          </a>
        </nav>

          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {isMenuOpen && (
          <div className="md:hidden bg-black/90 backdrop-blur-lg border-t border-white/10">
            <div className="px-4 py-6 space-y-4">
              {['Services', 'Products', 'Research', 'News', 'About'].map((item) => (
                <a
                  key={item}
                  href={`/#${item.toLowerCase()}`}
                  className="block text-white hover:text-red-400 transition-colors py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item}
                </a>
              ))}
              <a
                href="/expert-in-the-loop"
                className="block text-white hover:text-green-400 transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Expert-in-the-Loop™
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  )

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="px-6 py-24 pt-32">
    {/* Intro Section */}
    <section className="max-w-4xl mx-auto text-center">
      <h1 className="text-5xl font-bold mb-6">
        <span className="bg-gradient-to-r from-white via-red-300 to-pink-400 bg-clip-text text-transparent">
          Expert-in-the-Loop™
        </span>
      </h1>
      <p className="text-lg text-white/80 mb-12">
        Transform professional expertise into automated intelligence. ForGen AI's Expert-in-the-Loop™ breaks down complex domain-specific tasks into manageable subtasks, then helps automate them through our Map-Reduce-Produce methodology.
      </p>
    </section>

    {/* Map-Reduce-Produce Explanation */}
    <section className="max-w-5xl mx-auto mb-24">
      <div className="bg-gradient-to-r from-red-900/20 to-pink-900/20 border border-red-500/30 rounded-xl p-8 mb-12">
        <h2 className="text-3xl font-bold mb-6 text-center">Map-Reduce-Produce Methodology</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <Target className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Map</h3>
            <p className="text-white/70">Break complex professional tasks into discrete, automatable subtasks</p>
          </div>
          <div className="text-center">
            <Layers className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Reduce</h3>
            <p className="text-white/70">Process subtasks through ForGen's tool, agent, and pipeline framework</p>
          </div>
          <div className="text-center">
            <Zap className="w-12 h-12 text-pink-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Produce</h3>
            <p className="text-white/70">Generate actionable outputs that experts can validate and refine</p>
          </div>
        </div>
      </div>
    </section>

    {/* How It Works Section */}
    <section className="max-w-5xl mx-auto mb-24">
      <h2 className="text-3xl font-bold mb-12 text-center">How Expert-in-the-Loop™ Works</h2>
      <div className="space-y-8">
        <div className="bg-white/5 border border-white/10 rounded-xl p-8">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <span className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 text-sm">1</span>
            Task Decomposition
          </h3>
          <p className="text-white/70 pl-12">
            Experts collaborate with the ForGen Team and/or use our task decomposition tool to break down complex domain-specific workflows into smaller, well-defined subtasks that can be systematically automated.
          </p>
        </div>
        
        <div className="bg-white/5 border border-white/10 rounded-xl p-8">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <span className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 text-sm">2</span>
            Automated Subtask Processing
          </h3>
          <p className="text-white/70 pl-12">
            ForGen's agentic, automated orchestration framework simplifies task orchestration through marshalling each subtask into one of a tool, agent, or pipeline.  ForGen's agentic orchestration framework processes these subtasks with the expert's guidance in order to devise customized, well-defined workflows that meet your needs, all while maintaining strict schema validation and resource cost (AI usage) tracking throughout the execution.
          </p>
        </div>
        
        <div className="bg-white/5 border border-white/10 rounded-xl p-8">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <span className="bg-pink-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 text-sm">3</span>
            Expert Validation & Refinement
          </h3>
          <p className="text-white/70 pl-12">
            Experts remain <i>in the loop</i> to validate outputs, provide feedback, and refine the automation process, ensuring quality and domain accuracy.
          </p>
        </div>
      </div>
    </section>

    {/* Core Benefits Section */}
    <section className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto mb-24">
      {[
        {
          icon: <UserCheck className="w-10 h-10 text-red-400" />,
          title: "Domain Expertise Preserved",
          text: "Professional knowledge and judgment remain central to the automation process, ensuring outputs meet real-world standards.",
        },
        {
          icon: <Brain className="w-10 h-10 text-purple-400" />,
          title: "Scalable Task Automation",
          text: "Once decomposed, professional workflows can be automated at scale while maintaining quality through expert oversight.",
        },
        {
          icon: <Sparkles className="w-10 h-10 text-pink-400" />,
          title: "Continuous Improvement",
          text: "The system learns from expert feedback, continuously improving automation accuracy and expanding capabilities.",
        },
      ].map((item, i) => (
        <div key={i} className="bg-white/5 border border-white/10 p-8 rounded-xl text-center hover:border-red-500/30">
          <div className="mb-4">{item.icon}</div>
          <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
          <p className="text-white/70">{item.text}</p>
        </div>
      ))}
    </section>

    {/* Framework Integration */}
    <section className="max-w-5xl mx-auto">
      <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-xl p-8">
        <h2 className="text-2xl font-bold mb-6 text-center">Powered by ForGen Framework</h2>
        <div className="grid md:grid-cols-3 gap-6 text-center mb-8">
          <div>
            <h3 className="font-bold text-red-400 mb-2">Tools</h3>
            <p className="text-sm text-white/70">Phase-based execution with schema validation</p>
          </div>
          <div>
            <h3 className="font-bold text-purple-400 mb-2">Agents</h3>
            <p className="text-sm text-white/70">Generative AI with cost tracking and dynamic execution</p>
          </div>
          <div>
            <h3 className="font-bold text-pink-400 mb-2">Pipelines</h3>
            <p className="text-sm text-white/70">Sequential and DAG-based workflow orchestration</p>
          </div>
        </div>
        
        {/* US Development Callout */}
        <div className="relative bg-gradient-to-br from-blue-900/30 via-indigo-900/20 to-blue-800/30 border border-blue-400/40 rounded-xl p-8 overflow-hidden">
          {/* Background security pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-4 left-4 w-16 h-16 border-2 border-blue-300 rounded-full"></div>
            <div className="absolute top-8 right-8 w-12 h-12 border-2 border-blue-300 rounded-lg rotate-45"></div>
            <div className="absolute bottom-4 left-8 w-8 h-8 border-2 border-blue-300 rounded-full"></div>
            <div className="absolute bottom-8 right-4 w-10 h-10 border-2 border-blue-300 rounded-lg rotate-12"></div>
          </div>
          
          {/* Header with shield */}
          <div className="relative z-10 flex items-center justify-center mb-8">
            <div className="relative">
              <Shield className="w-12 h-12 text-blue-300 drop-shadow-lg" />
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full flex items-center justify-center">
                <CheckCircle className="w-3 h-3 text-green-900" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-blue-200 ml-4 tracking-wide">BUILT IN AMERICA</h3>
          </div>
          
          {/* Content cards */}
          <div className="relative z-10 grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-800/40 to-blue-900/60 rounded-lg p-6 border border-blue-500/30 hover:border-blue-400/50 transition-all duration-300">
              <div className="flex items-center mb-3">
                <Code className="w-6 h-6 text-blue-300 mr-3" />
                <h4 className="font-bold text-blue-200">Custom In-House Code</h4>
              </div>
              <p className="text-blue-100/80 mb-1">100% proprietary Python framework</p>
              <p className="text-blue-300/60 text-sm">Developed from scratch by our team</p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-800/40 to-blue-900/60 rounded-lg p-6 border border-blue-500/30 hover:border-blue-400/50 transition-all duration-300">
              <div className="flex items-center mb-3">
                <MapPin className="w-6 h-6 text-blue-300 mr-3" />
                <h4 className="font-bold text-blue-200">All-U.S. Team</h4>
              </div>
              <p className="text-blue-100/80 mb-1">Domestic development workforce</p>
              <p className="text-blue-300/60 text-sm">Headquartered in Metro-Detroit, Michigan</p>
            </div>
          </div>
          
          {/* Bottom security badge */}
          <div className="relative z-10 mt-6 flex items-center justify-center">
            <div className="bg-blue-800/50 px-4 py-2 rounded-full border border-blue-400/30">
              <span className="text-blue-200 text-sm font-medium">SECURE • TRUSTED • AMERICAN-MADE</span>
            </div>
          </div>
        </div>
      </div>
    </section>
      </main>
    </div>
  )
}

export default ExpertInTheLoop
